import { Fragment, useCallback, useEffect, useState } from "react";
import { API_BASE_URL } from "../utils/config";
import CustomSelect from "../components/CustomSelect";

const PAGE_SIZE = 30;
const AUTO_REFRESH_MS = 5000;

const STATUS_OPTIONS = [
  { value: "ALL",   label: "Semua status" },
  { value: "error", label: "Error (4xx + 5xx)" },
  { value: "5xx",   label: "5xx — server error" },
  { value: "4xx",   label: "4xx — ditolak / salah input" },
  { value: "3xx",   label: "3xx" },
  { value: "2xx",   label: "2xx — sukses" },
];

const METHOD_OPTIONS = ["ALL", "GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => ({
  value: m,
  label: m === "ALL" ? "Semua method" : m,
}));

const METHOD_BADGE = {
  GET:    "badge-info",
  POST:   "badge-success",
  PUT:    "badge-warning",
  PATCH:  "badge-warning",
  DELETE: "badge-danger",
};

const statusBadge = (code) => {
  if (code >= 500) return "badge-danger";
  if (code >= 400) return "badge-warning";
  if (code >= 300) return "badge-info";
  return "badge-success";
};

const fmtTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-SG", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
        second: "2-digit", hour12: false,
      })
    : "-";

// Request bodies are stored as compact JSON; show them readable when they parse.
const prettyJson = (text) => {
  try { return JSON.stringify(JSON.parse(text), null, 2); } catch { return text; }
};

function Block({ label, children, tone }) {
  return (
    <div>
      <div className="eyebrow mb-1">{label}</div>
      <pre
        className="rounded-md p-3 text-[12px] font-mono whitespace-pre-wrap break-all max-h-[320px] overflow-auto"
        style={{
          background: "var(--bg-base)",
          border: "1px solid var(--border-subtle)",
          color: tone === "danger" ? "var(--danger)" : "var(--text-soft)",
        }}
      >
        {children}
      </pre>
    </div>
  );
}

function DebugLog() {
  const [logs, setLogs]           = useState([]);
  const [totalCount, setTotal]    = useState(0);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [page, setPage]           = useState(1);
  const [expandedId, setExpanded] = useState(null);
  const [autoRefresh, setAuto]    = useState(false);

  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [methodFilter, setMethodFilter] = useState("ALL");
  const [dateFrom, setDateFrom]         = useState("");
  const [dateTo, setDateTo]             = useState("");

  useEffect(() => { setPage(1); }, [search, statusFilter, methodFilter, dateFrom, dateTo]);

  // `silent` = background poll: keep the rows on screen instead of flashing "Memuat…".
  const fetchLogs = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    setError("");

    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
    if (search.trim()) params.set("search", search.trim());
    if (statusFilter !== "ALL") params.set("status", statusFilter);
    if (methodFilter !== "ALL") params.set("method", methodFilter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);

    fetch(`${API_BASE_URL}/api/debug-logs/?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 403) throw new Error("Hanya superadmin yang bisa melihat debug log.");
          const data = await res.json().catch(() => null);
          throw new Error(data?.detail || "Gagal memuat debug log.");
        }
        return res.json();
      })
      .then((data) => {
        const results = Array.isArray(data) ? data : data.results || [];
        setLogs(results);
        setTotal(typeof data?.count === "number" ? data.count : results.length);
      })
      .catch((err) => setError(err.message || "Gagal memuat debug log."))
      .finally(() => setLoading(false));
  }, [page, search, statusFilter, methodFilter, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = window.setInterval(() => fetchLogs(true), AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [autoRefresh, fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasFilter = search || statusFilter !== "ALL" || methodFilter !== "ALL" || dateFrom || dateTo;

  const resetFilters = () => {
    setSearch(""); setStatusFilter("ALL"); setMethodFilter("ALL"); setDateFrom(""); setDateTo("");
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1.5">
          <h1 className="page-title">Debug Log</h1>
          <p className="text-[14px]" style={{ color: "var(--text-secondary)" }}>
            Setiap request API tercatat otomatis — user, status, durasi, body (password disamarkan),
            dan traceback kalau server error. IP tidak disimpan. Dibersihkan otomatis.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <span className="text-[12px]" style={{ color: "var(--text-secondary)" }}>{totalCount} request</span>
          <label className="flex cursor-pointer items-center gap-2 text-[13px]" style={{ color: "var(--text-secondary)" }}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAuto(e.target.checked)} />
            Auto-refresh 5 dtk
          </label>
          <button type="button" onClick={() => fetchLogs()} className="btn-outline">Refresh</button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl px-4 py-3 text-[13px] border"
          style={{ color: "var(--danger)", background: "var(--danger-subtle)", borderColor: "color-mix(in srgb, var(--danger) 30%, transparent)" }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:max-w-xs">
          <label className="mb-1.5 block text-[12px]" style={{ color: "var(--text-secondary)" }}>Search</label>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            className="glass-input" placeholder="user, path, body, error…" />
        </div>
        <div className="min-w-[180px]">
          <label className="mb-1.5 block text-[12px]" style={{ color: "var(--text-secondary)" }}>Status</label>
          <CustomSelect value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
        </div>
        <div className="min-w-[140px]">
          <label className="mb-1.5 block text-[12px]" style={{ color: "var(--text-secondary)" }}>Method</label>
          <CustomSelect value={methodFilter} onChange={setMethodFilter} options={METHOD_OPTIONS} />
        </div>
        <div>
          <label className="mb-1.5 block text-[12px]" style={{ color: "var(--text-secondary)" }}>Dari tanggal</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="glass-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-[12px]" style={{ color: "var(--text-secondary)" }}>Sampai tanggal</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="glass-input" />
        </div>
        {hasFilter && (
          <button type="button" onClick={resetFilters} className="btn-danger self-end">Reset filter</button>
        )}
      </div>

      {/* Table */}
      <div className="glass-table-wrap">
        <table className="glass-table min-w-[820px]">
          <thead>
            <tr>
              <th>Waktu</th>
              <th>User</th>
              <th>Method</th>
              <th>Path</th>
              <th>Status</th>
              <th className="text-right">Durasi</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-muted">Memuat debug log…</td></tr>
            )}
            {!loading && logs.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-muted">Belum ada request tercatat.</td></tr>
            )}
            {!loading && logs.map((log) => {
              const open = expandedId === log.id;
              const hasDetail = !!(log.request_body || log.response_excerpt || log.error || log.query);
              return (
                <Fragment key={log.id}>
                  <tr
                    onClick={() => setExpanded(open ? null : log.id)}
                    className="cursor-pointer"
                    aria-expanded={open}
                  >
                    <td className="text-[12px]" style={{ color: "var(--text-secondary)" }}>{fmtTime(log.created_at)}</td>
                    <td className="text-[13px] font-medium">{log.username || "-"}</td>
                    <td>
                      <span className={`badge ${METHOD_BADGE[log.method] || "badge-info"}`}>{log.method}</span>
                    </td>
                    <td className="font-mono text-[12px]" style={{ color: "var(--text-soft)" }}>
                      <span className="block max-w-[420px] truncate" title={log.path + (log.query ? `?${log.query}` : "")}>
                        {log.path}
                        {log.query && <span style={{ color: "var(--text-faint)" }}>?{log.query}</span>}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${statusBadge(log.status_code)}`}>{log.status_code}</span>
                    </td>
                    <td className="text-right font-mono text-[12px]"
                      style={{ color: log.duration_ms >= 1000 ? "var(--amber)" : "var(--text-secondary)" }}>
                      {log.duration_ms} ms
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={6} className="px-5 py-4" style={{ background: "var(--bg-surface-hover)" }}>
                        <div className="mb-3 text-[12px]" style={{ color: "var(--text-secondary)" }}>
                          view: <span className="font-mono">{log.view_name || "-"}</span>
                        </div>
                        {!hasDetail && (
                          <div className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
                            Tidak ada body, query, atau error untuk request ini.
                          </div>
                        )}
                        <div className="space-y-3">
                          {log.error && <Block label="Traceback" tone="danger">{log.error}</Block>}
                          {log.response_excerpt && <Block label="Response">{prettyJson(log.response_excerpt)}</Block>}
                          {log.request_body && <Block label="Request body">{prettyJson(log.request_body)}</Block>}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button type="button" className="btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(1)}>First</button>
          <button type="button" className="btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
        </div>
        <span className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
          Halaman {page} dari {totalPages} ({totalCount} total)
        </span>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-outline btn-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          <button type="button" className="btn-outline btn-sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>Last</button>
        </div>
      </div>
    </div>
  );
}

export default DebugLog;
